import {
  createElement,
  forwardRef,
  ForwardedRef,
  RefAttributes,
  ForwardRefExoticComponent,
  PropsWithoutRef,
  ComponentType
} from 'react';
import {
  ErrorBoundary,
  ErrorBoundaryPropsWithComponent
} from '@kyruus/error-boundary';
import { useTrackBoundaryError } from '../../lib/hooks';
import ErrorFallback from './error-fallback';

export function withErrorBoundary<Props extends object>(
  component: ComponentType<Props>,
  errorBoundaryProps?: ErrorBoundaryPropsWithComponent
): ForwardRefExoticComponent<PropsWithoutRef<Props> & RefAttributes<any>> {
  const name = component.displayName || component.name || 'Unknown';

  const WrappedInner = (
    props: Props,
    ref: ForwardedRef<ComponentType<Props>>
  ) => {
    const onError = useTrackBoundaryError(name);
    const newErrorBoundaryProps: ErrorBoundaryPropsWithComponent = {
      FallbackComponent: ErrorFallback,
      onError,
      ...errorBoundaryProps
    };

    return createElement(
      ErrorBoundary,
      newErrorBoundaryProps,
      createElement(component, { ...props, ref })
    );
  };

  const Wrapped = forwardRef<ComponentType<Props>, Props>(
    WrappedInner
  ) as unknown as ForwardRefExoticComponent<
    PropsWithoutRef<Props> & RefAttributes<any>
  >;

  Wrapped.displayName = `withErrorBoundary(${name})`;

  return Wrapped;
}
